import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Campaign,
  CheckCircle,
  Group,
  QueueMusic,
  RadioButtonChecked,
  Refresh,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import VoicetrackSessionPanel from '../live/VoicetrackSessionPanel';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const formatLocalDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const clockTime = (hour) => `${String(hour).padStart(2, '0')}:00`;

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const formatTimeOfDay = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const headerCellSx = {
  color: 'var(--rs-muted)',
  fontWeight: 700,
  fontSize: '0.68rem',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderBottom: '1px solid rgba(148,163,184,0.14)',
  bgcolor: 'var(--rs-overlay-strong)',
  whiteSpace: 'nowrap',
};

const bodyCellSx = {
  fontSize: '0.85rem',
  borderBottom: '1px solid rgba(148,163,184,0.08)',
};

const categoryFor = (item) => {
  if (!item) return { name: 'Item', color: '#64748b' };
  if (item.type === 'voicetrack' || item.type === 'voice') return { name: 'Voicetrack', color: '#a855f7' };
  if (item.type === 'ai_voicetrack') return { name: 'AI Voicetrack', color: '#a855f7' };
  if (item.type === 'commercial_break') return { name: 'Ads', color: '#f59e0b' };
  if (item.type === 'stream_relay') return { name: 'Relay', color: '#607d8b' };
  if (item.type === 'hard_fixed_timer') return { name: 'Hard Timer', color: '#ef4444' };
  if (item.category?.name) {
    return { name: item.category.name, color: item.category.color || '#22d3ee' };
  }
  return { name: 'Music', color: '#22d3ee' };
};

const isVoicetrackItem = (item) => item?.type === 'voicetrack' || item?.type === 'voice';

/**
 * Multi Voicetrack.
 *
 * The ordinary Voicetrack page is one marker at a time: open it, record, save.
 * A show with several presenters needs the opposite shape — one room the whole
 * team stays in, while the presenter works through the voicetrack slots for as
 * many hours (or days) as the show needs, recording each as its own take.
 *
 * Changing the hour or date reloads the log but deliberately does NOT tear the
 * session down: the session belongs to the page, not to the hour being viewed.
 */
export default function MultiVoicetrack() {
  const { user } = useAuth();
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState('');
  const [selectedDate, setSelectedDate] = useState(formatLocalDate);
  const [selectedHour, setSelectedHour] = useState(new Date().getHours());
  const [playlist, setPlaylist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [collaboration, setCollaboration] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [takes, setTakes] = useState([]);
  const [roomId, setRoomId] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);

  const selectedMarker = useMemo(() => {
    if (selectedIndex === null) return null;
    const item = playlist[selectedIndex];
    return item && isVoicetrackItem(item) ? { item, index: selectedIndex } : null;
  }, [playlist, selectedIndex]);

  // Stations are listed once. The user's own station is the default; the first
  // listed one is used only when nothing is selected by the time they arrive.
  const userStationId = user?.stationId || null;
  useEffect(() => {
    let cancelled = false;
    if (userStationId) setSelectedStation((current) => current || userStationId);
    const loadStations = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/stations`, { withCredentials: true });
        if (cancelled) return;
        const list = res.data || [];
        setStations(list);
        if (list.length) setSelectedStation((current) => current || list[0].id);
      } catch {
        if (!cancelled) setError('Could not load stations.');
      }
    };
    loadStations();
    return () => { cancelled = true; };
  }, [userStationId]);

  // The clock setting can differ per hour, so it is re-checked whenever the
  // hour being viewed changes — but that only gates the UI, never the session.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!selectedStation) return;
      try {
        const res = await axios.get(`${API_URL}/api/live-studio/voicetrack/availability`, {
          params: { stationId: selectedStation, date: selectedDate, hour: selectedHour },
          withCredentials: true,
        });
        if (!cancelled) setCollaboration(res.data || null);
      } catch {
        if (!cancelled) setCollaboration(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedStation, selectedDate, selectedHour]);

  // The log for the hour on screen — the same data the Voicetrack page shows.
  useEffect(() => {
    let cancelled = false;
    const loadPlaylist = async () => {
      if (!selectedStation) return;
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(
          `${API_URL}/api/playlist-generator/${selectedStation}/${selectedDate}/${selectedHour}`,
          { withCredentials: true }
        );
        if (!cancelled) setPlaylist(res.data?.items || []);
      } catch (requestError) {
        if (!cancelled) {
          // A missing hour simply has no generated playlist yet.
          setPlaylist([]);
          if (requestError?.response?.status !== 404) {
            setError('Could not load the log for this hour.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadPlaylist();
    return () => { cancelled = true; };
  }, [selectedStation, selectedDate, selectedHour, refreshToken]);

  // Keep the existing session alive across hour changes. This deliberately
  // looks up the room by station only, so browsing to another hour does not
  // make the page think the session has gone away.
  useEffect(() => {
    let cancelled = false;
    const loadSession = async () => {
      if (!selectedStation) return;
      try {
        const res = await axios.get(`${API_URL}/api/live-studio/rooms/active`, {
          params: { stationId: selectedStation, purpose: 'voicetrack' },
          withCredentials: true,
        });
        const activeId = res.data?.id || null;
        if (cancelled) return;
        setRoomId(activeId);
        if (!activeId) {
          setTakes([]);
          return;
        }
        const rec = await axios.get(
          `${API_URL}/api/live-studio/rooms/${activeId}/recording`,
          { withCredentials: true }
        );
        if (!cancelled) setTakes(rec.data?.takes || []);
      } catch {
        if (!cancelled) {
          setRoomId(null);
          setTakes([]);
        }
      }
    };
    loadSession();
    return () => { cancelled = true; };
  }, [selectedStation, refreshToken]);

  /**
   * Which markers already have a take, keyed by hour and slot. A take records
   * the marker it was made for, so the log can show what is done.
   */
  const recordedKeys = useMemo(() => {
    const keys = new Set();
    for (const take of takes) {
      if (take.status === 'claimed') continue;
      const context = take.session_context || {};
      if (context.playlist_date == null || context.playlist_hour == null) continue;
      keys.add(`${context.playlist_date}-${context.playlist_hour}-${context.playlist_index}`);
    }
    return keys;
  }, [takes]);

  const currentKey = (index) => `${selectedDate}-${selectedHour}-${index}`;
  const savedForHour = useMemo(() => {
    const keys = new Set();
    for (const take of takes) {
      if (take.status !== 'claimed') continue;
      const context = take.session_context || {};
      if (String(context.playlist_date) !== String(selectedDate)) continue;
      if (Number(context.playlist_hour) !== Number(selectedHour)) continue;
      keys.add(context.playlist_index);
    }
    return keys;
  }, [takes, selectedDate, selectedHour]);

  // Start/stop/end of takes must not disturb the session, so the panel is
  // mounted once per station rather than once per hour.
  const sessionContext = useMemo(() => ({
    playlist_date: selectedDate,
    playlist_hour: selectedHour,
    playlist_index: selectedMarker?.index ?? null,
    playout_log_id: selectedMarker?.item?.id ?? null,
  }), [selectedDate, selectedHour, selectedMarker]);

  const canUseSessions = collaboration?.enabled === true;

  return (
    <Box>
      <PageHeader
        overline="Presenting"
        title="Multi Voicetrack"
        subtitle="One live session for the whole show — record each voicetrack slot as its own take"
        icon={<Group />}
      />

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '340px 1fr' }, gap: 2, alignItems: 'start' }}>
        {/* Left: session and hour selection. Stays mounted across hour changes. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
            <CardContent>
              <Typography sx={{ fontWeight: 700, mb: 2 }}>The hour</Typography>
              {stations.length > 1 && (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={selectedStation || ''}
                    label="Station"
                    onChange={(event) => {
                      setSelectedStation(event.target.value);
                      setSelectedIndex(null);
                    }}
                  >
                    {stations.map((station) => (
                      <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                fullWidth
                type="date"
                size="small"
                label="Date"
                value={selectedDate}
                onChange={(event) => {
                  setSelectedDate(event.target.value);
                  setSelectedIndex(null);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Hour</InputLabel>
                <Select
                  value={selectedHour}
                  label="Hour"
                  onChange={(event) => {
                    setSelectedHour(Number(event.target.value));
                    setSelectedIndex(null);
                  }}
                >
                  {Array.from({ length: 24 }, (_, hour) => (
                    <MenuItem key={hour} value={hour}>{clockTime(hour)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={refresh}
                sx={{ mt: 2 }}
              >
                Reload log
              </Button>
              {roomId && (
                <Alert severity="success" icon={<RadioButtonChecked />} sx={{ mt: 2 }}>
                  Session open. Changing the hour keeps everyone connected.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Always mounted: unmounting disconnects the host from the session,
              so an hour where co-presenting is off (or a failed availability
              check) only disables starting a session or a take. */}
          <VoicetrackSessionPanel
            stationId={selectedStation}
            sessionContext={sessionContext}
            available={canUseSessions}
            unavailableReason={collaboration?.reason
              || 'Multi Voicetrack needs co-presented voicetracks enabled for this hour.'}
            onSaved={() => {
              setNotice('Voicetrack saved to this slot.');
              setSelectedIndex(null);
              refresh();
            }}
            onTakeFinished={() => refresh()}
          />
        </Box>

        {/* Right: the hour's log, in the same shape as the Voicetrack page. */}
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <QueueMusic sx={{ color: 'var(--rs-accent)' }} />
              <Typography sx={{ fontWeight: 700, flex: 1 }}>
                {selectedDate} · {clockTime(selectedHour)}
              </Typography>
              <Chip size="small" label={`${playlist.length} items`} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Select a voicetrack row to choose the slot the next take records into.
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 6, justifyContent: 'center' }}>
                <CircularProgress size={22} />
                <Typography color="text.secondary">Loading log…</Typography>
              </Box>
            ) : playlist.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(168,85,247,0.12)',
                    border: '1px solid rgba(192,132,252,0.3)',
                  }}
                >
                  <Campaign sx={{ fontSize: 28, color: '#c084fc' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>No log for this hour</Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Generate the playlist for this hour, then the voicetrack slots will appear here.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 'calc(var(--rs-vh) - 320px)', borderRadius: 2 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>#</TableCell>
                      <TableCell sx={headerCellSx}>Time</TableCell>
                      <TableCell sx={headerCellSx}>Type</TableCell>
                      <TableCell sx={headerCellSx}>Title</TableCell>
                      <TableCell sx={headerCellSx}>Artist</TableCell>
                      <TableCell sx={headerCellSx}>Duration</TableCell>
                      <TableCell sx={headerCellSx} align="right">Slot</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {playlist.map((item, index) => {
                      const voicetrack = isVoicetrackItem(item);
                      const category = categoryFor(item);
                      const recorded = voicetrack && recordedKeys.has(currentKey(index));
                      const saved = voicetrack && savedForHour.has(index);
                      const selected = selectedIndex === index;
                      return (
                        <TableRow
                          key={`${index}-${item.id ?? item.title}`}
                          hover={voicetrack}
                          selected={selected}
                          onClick={voicetrack ? () => setSelectedIndex(selected ? null : index) : undefined}
                          sx={{
                            cursor: voicetrack ? 'pointer' : 'default',
                            bgcolor: voicetrack ? 'rgba(168,85,247,0.06)' : undefined,
                            '& td:first-of-type': voicetrack
                              ? { borderLeft: '3px solid rgba(168,85,247,0.5)' }
                              : {},
                          }}
                        >
                          <TableCell sx={{ ...bodyCellSx, color: 'var(--rs-muted)', width: 44 }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums', width: 92 }}>
                            {formatTimeOfDay(item.startTime)}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, width: 140 }}>
                            <Chip
                              size="small"
                              label={category.name}
                              sx={{
                                bgcolor: `${category.color}22`,
                                color: category.color,
                                border: `1px solid ${category.color}55`,
                                fontSize: '0.62rem',
                                height: 20,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontWeight: voicetrack ? 700 : 500 }}>
                            {item.title || 'Untitled'}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, color: 'var(--rs-muted)' }}>
                            {item.artist || '—'}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums', width: 80 }}>
                            {formatDuration(item.duration)}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, width: 130 }} align="right">
                            {voicetrack && saved ? (
                              <Chip size="small" color="success" icon={<CheckCircle />} label="Saved" />
                            ) : voicetrack && recorded ? (
                              <Chip size="small" color="warning" label="Take ready" />
                            ) : voicetrack && selected ? (
                              <Chip size="small" color="error" icon={<RadioButtonChecked />} label="Selected" />
                            ) : voicetrack ? (
                              <Typography variant="caption" color="text.secondary">Record</Typography>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
